import type { ReactNode, SyntheticEvent } from 'react';
import { Link } from 'react-router';
import { Icon, type IconName } from '../components/icons';
import { euro } from '../format';
import type { LocalSale } from '../pos/types';
import { Sheet, type SyncView } from './posParts';

/**
 * Kopfzeile der Kasse (U17a): Kassenname klein, danach nur Symbolknöpfe mit Popups. Jeder Knopf ist mindestens 48 × 48 px
 * groß und trägt einen Namen (`aria-label`, Zahl inklusive), dazu `title` für die Maus. Die Seite entscheidet, was ein
 * Tipp öffnet; die Kopfzeile zeigt nur an und meldet den Tipp zurück.
 */

/** „Parken, 2 Körbe geparkt“ (die Zahl steht mit im Namen, nicht nur als Abzeichen). */
export function parkLabel(parkedCount: number): string {
  if (parkedCount === 0) return 'Parken';
  return `Parken, ${String(parkedCount)} ${parkedCount === 1 ? 'Korb' : 'Körbe'} geparkt`;
}

/** „Letzter Verkauf Nr. 41, 13,50 Euro, Gesendet 14:31“ (der Sendestatus steht mit im Namen). */
export function lastSaleLabel(sale: LocalSale | undefined, status?: string): string {
  if (sale === undefined) return 'Letzter Verkauf (noch keiner)';
  const state = sale.voided ? 'storniert' : status;
  return `Letzter Verkauf Nr. ${String(sale.registerSeq)}, ${euro(sale.totalCents).replace('€', 'Euro')}${state === undefined || state === '' ? '' : `, ${state}`}`;
}

/** „Online, 3 Verkäufe warten“ (Zustand und wartende Verkäufe als Name des Status-Knopfs). */
export function statusLabel(view: SyncView, extra?: string): string {
  const waiting =
    view.waiting === 0
      ? ''
      : `, ${String(view.waiting)} ${view.waiting === 1 ? 'Verkauf wartet' : 'Verkäufe warten'}`;
  return `${view.label}${waiting}${extra === undefined || extra === '' ? '' : `, ${extra}`}`;
}

function IconButton({
  icon,
  label,
  badge,
  popup = false,
  pressed,
  disabled,
  className = '',
  onClick,
  children,
}: {
  icon?: IconName;
  label: string;
  /** Zahl am Symbol (auch im Namen enthalten). */
  badge?: number | undefined;
  popup?: boolean;
  pressed?: boolean;
  disabled?: boolean;
  className?: string;
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`cash__hd ${className}`.trim()}
      aria-label={label}
      title={label}
      aria-haspopup={popup ? 'dialog' : undefined}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
    >
      {icon !== undefined && <Icon name={icon} size={24} />}
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="cash__hd-badge" aria-hidden="true">
          {badge}
        </span>
      )}
    </button>
  );
}

export function PosHeader({
  title,
  view,
  statusExtra,
  statusWarning = false,
  parkedCount,
  lastSale,
  lastSaleStatus,
  sound,
  onPark,
  onLast,
  onStatus,
  onMenu,
}: {
  title: string;
  view: SyncView;
  /** Zusatz im Namen des Status-Knopfs (z. B. „Artikelstand 10:42“). */
  statusExtra?: string | undefined;
  /** Warnzeichen am Status-Punkt (leerer oder alter Artikelstand). */
  statusWarning?: boolean;
  parkedCount: number;
  lastSale: LocalSale | undefined;
  /** Sendestatus des letzten Verkaufs für den Namen des Symbols („Gesendet 14:31“). */
  lastSaleStatus?: string | undefined;
  /** Ton-Schalter in der Kopfzeile: nur ohne Kamerabild, sonst liegt er im Bild (nie doppelt). */
  sound?: { on: boolean; onToggle: () => void } | undefined;
  onPark: () => void;
  onLast: () => void;
  onStatus: () => void;
  onMenu: () => void;
}) {
  const label = statusLabel(view, statusExtra);
  return (
    <header className="cash__head">
      <h1 className="cash__title">{title}</h1>
      <IconButton
        icon="park"
        label={parkLabel(parkedCount)}
        badge={parkedCount}
        popup
        onClick={onPark}
      />
      <IconButton
        icon="undo"
        label={lastSaleLabel(lastSale, lastSaleStatus)}
        popup
        disabled={lastSale === undefined}
        onClick={onLast}
      >
        {lastSale !== undefined && (
          <span
            className="cash__hd-text"
            aria-hidden="true"
          >{`Nr. ${String(lastSale.registerSeq)}`}</span>
        )}
      </IconButton>
      {sound !== undefined && (
        <IconButton
          icon={sound.on ? 'volume' : 'volume-off'}
          label={sound.on ? 'Ton ausschalten' : 'Ton einschalten'}
          pressed={sound.on}
          onClick={sound.onToggle}
        />
      )}
      <IconButton label={label} badge={view.waiting} popup onClick={onStatus}>
        <span className={`cash__status cash__status--${view.tone}`} aria-hidden="true" />
        {statusWarning && (
          <span className="cash__hd-warn" aria-hidden="true">
            <Icon name="alert" size={14} />
          </span>
        )}
        <span className="cash__hd-text" aria-hidden="true">
          {view.label}
        </span>
      </IconButton>
      <IconButton icon="more" label="Menü" popup onClick={onMenu} />
    </header>
  );
}

function MenuButton({
  icon,
  disabled,
  onClick,
  children,
}: {
  icon: IconName;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <li>
      <button type="button" className="cash__menu-item" disabled={disabled} onClick={onClick}>
        <Icon name={icon} size={22} />
        <span>{children}</span>
      </button>
    </li>
  );
}

function MenuLink({
  icon,
  to,
  onClick,
  children,
}: {
  icon: IconName;
  to: string;
  onClick: (event: SyntheticEvent) => void;
  children: ReactNode;
}) {
  return (
    <li>
      <Link className="cash__menu-item" to={to} onClick={onClick}>
        <Icon name={icon} size={22} />
        <span>{children}</span>
      </Link>
    </li>
  );
}

function MenuSwitch({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  children: ReactNode;
}) {
  return (
    <li>
      <label className="cash__menu-item">
        <input
          type="checkbox"
          checked={checked}
          onChange={(change) => {
            onChange(change.target.checked);
          }}
        />
        <span>{children}</span>
      </label>
    </li>
  );
}

/**
 * Menü-Blatt hinter „⋯“ (U17a), gegliedert in Kasse, Erfassen, Gerät, Schicht (nur mit Konto) und Schnellzugang. Einträge,
 * die Netz brauchen, sind offline gesperrt und nennen den Grund.
 */
export function PosMenuSheet({
  eventId,
  registerId,
  deviceMode,
  offline,
  auto,
  onAuto,
  sound,
  onSound,
  canClear,
  canCustomer,
  canFullscreen,
  onAll,
  onClear,
  onCustomer,
  onDevice,
  onExport,
  onFullscreen,
  onHandover,
  onLeaveLink,
  onNote,
  onNoLabel,
  onClose,
}: {
  eventId: string;
  /** Eigene Kasse: „Bargeld zählen“ springt mit `#kasse-{registerId}` zu ihrer Karte (U18i). */
  registerId?: string | undefined;
  deviceMode: boolean;
  offline: boolean;
  auto: boolean;
  onAuto: (next: boolean) => void;
  sound: boolean;
  onSound: () => void;
  canClear: boolean;
  canCustomer: boolean;
  canFullscreen: boolean;
  onAll: () => void;
  onClear: () => void;
  onCustomer: () => void;
  onDevice: () => void;
  onExport: () => void;
  onFullscreen: () => void;
  onHandover: () => void;
  /** Vor dem Verlassen der Kasse über einen Link (fragt bei geparkten Körben nach, kann abbrechen). */
  onLeaveLink: (event: SyntheticEvent) => void;
  /** „Problem melden“ (U17f): Kommentar an die Basar-Leitung ohne Verkauf, geht auch offline (mit dem nächsten Abgleich). */
  onNote?: (() => void) | undefined;
  /** „Artikel ohne Etikett erfassen“ (U17h). */
  onNoLabel?: (() => void) | undefined;
  onClose: () => void;
}) {
  const closeAnd = (action: () => void) => () => {
    onClose();
    action();
  };
  return (
    <Sheet title="Menü" onClose={onClose}>
      <section className="cash__menu-group" aria-labelledby="menu-kasse">
        <h3 id="menu-kasse">Kasse</h3>
        <ul className="cash__menu-list">
          <MenuButton icon="list" onClick={closeAnd(onAll)}>
            Alle Verkäufe dieser Kasse
          </MenuButton>
          {onNoLabel !== undefined && (
            <MenuButton icon="tag" onClick={closeAnd(onNoLabel)}>
              Artikel ohne Etikett erfassen
            </MenuButton>
          )}
          <MenuButton icon="trash" disabled={!canClear} onClick={closeAnd(onClear)}>
            Warenkorb leeren
          </MenuButton>
          <MenuButton icon="monitor" disabled={!canCustomer} onClick={closeAnd(onCustomer)}>
            Kundenansicht
          </MenuButton>
          {onNote !== undefined && (
            <MenuButton icon="alert" onClick={closeAnd(onNote)}>
              Problem melden
            </MenuButton>
          )}
        </ul>
      </section>
      <section className="cash__menu-group" aria-labelledby="menu-erfassen">
        <h3 id="menu-erfassen">Erfassen</h3>
        <ul className="cash__menu-list">
          <MenuSwitch checked={auto} onChange={onAuto}>
            Automatisch erfassen
          </MenuSwitch>
          <MenuSwitch checked={sound} onChange={onSound}>
            Ton
          </MenuSwitch>
        </ul>
      </section>
      <section className="cash__menu-group" aria-labelledby="menu-geraet">
        <h3 id="menu-geraet">Gerät</h3>
        <ul className="cash__menu-list">
          <MenuButton icon="shield" onClick={closeAnd(onDevice)}>
            Geräte-Check
          </MenuButton>
          {canFullscreen && (
            <MenuButton icon="fullscreen" onClick={closeAnd(onFullscreen)}>
              Vollbild
            </MenuButton>
          )}
          <MenuButton icon="download" onClick={closeAnd(onExport)}>
            Notausgang: Daten als Datei sichern
          </MenuButton>
        </ul>
      </section>
      {!deviceMode && (
        <section className="cash__menu-group" aria-labelledby="menu-schicht">
          <h3 id="menu-schicht">Schicht</h3>
          <ul className="cash__menu-list">
            {offline ? (
              <MenuButton icon="wallet" disabled onClick={onClose}>
                Bargeld zählen (braucht Netz)
              </MenuButton>
            ) : (
              <MenuLink
                icon="wallet"
                to={`/basare/${eventId}/bargeld${registerId === undefined ? '' : `#kasse-${registerId}`}`}
                onClick={onLeaveLink}
              >
                Bargeld zählen
              </MenuLink>
            )}
            <MenuButton icon="swap" onClick={closeAnd(onHandover)}>
              Kasse übergeben
            </MenuButton>
            {offline ? (
              <MenuButton icon="home" disabled onClick={onClose}>
                Zum Basar (braucht Netz)
              </MenuButton>
            ) : (
              <MenuLink icon="home" to={`/basare/${eventId}`} onClick={onLeaveLink}>
                Zum Basar
              </MenuLink>
            )}
            {offline ? (
              <MenuButton icon="logout" disabled onClick={onClose}>
                Konto und Abmelden (braucht Netz)
              </MenuButton>
            ) : (
              <MenuLink icon="logout" to="/profil" onClick={onLeaveLink}>
                Konto und Abmelden
              </MenuLink>
            )}
          </ul>
        </section>
      )}
      {deviceMode && (
        <section className="cash__menu-group" aria-labelledby="menu-schnell">
          <h3 id="menu-schnell">Schnellzugang</h3>
          <ul className="cash__menu-list">
            <MenuLink icon="qr" to="/kasse-scannen" onClick={onClose}>
              Andere Kasse koppeln
            </MenuLink>
          </ul>
        </section>
      )}
    </Sheet>
  );
}
